import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';

/**
 * Aprovisionamiento de un TRASLADO.
 *
 * Son dos caminos y los decide el servidor, no esta pantalla:
 *
 * * **A — migración en un paso**: se consulta contra la OLT donde la ONT está
 *   hoy y, si se puede, se aprovisiona en la OLT del domicilio nuevo. Es
 *   asíncrono: la ejecución devuelve un id y el avance se sigue por el GET de
 *   estado del micro.
 * * **B — activación de siempre**: el mismo flujo de Activaciones de
 *   instalaciones (poner el contrato en ACTIVO). Se usa cuando A no se puede:
 *   no hay NAP en Softnet, el NAP no existe en EVA, la OLT no tiene IPv4 o no
 *   hay ONT elegida. El backend lo devuelve como `opcion: "B"` con el motivo.
 */

const { post, get } = erpApi();

export type OpcionAprovisionamiento = 'A' | 'B';

export type LadoOlt = {
  nap: string | null;
  nap_encontrado?: boolean;
  olt: string | null;
  olt_codigo?: string;
  ip_olt: string | null;
  /** Solo en la OLT actual: el nombre tal como lo reporta Softnet. */
  olt_softnet?: string | null;
};

/** Una serie dentro de la solicitud de migración. */
export type ResultadoSerie = {
  orden?: number;
  serie: string;
  estado: string;
  mensaje?: string | null;
  preparacion?: Record<string, unknown> | null;
};

/** Solicitud de migración (lo que devuelven el POST y el GET de estado). */
export type SolicitudMigracion = {
  id_solicitud: string;
  estado: string;
  completada: boolean;
  fase?: string | null;
  modo?: string;
  mensaje?: string | null;
  nombre_olt?: string;
  ip_olt?: string;
  resumen?: Record<string, number | Record<string, number>> | null;
  resultados?: ResultadoSerie[];
};

export type AprovisionamientoTraslado = {
  opcion: OpcionAprovisionamiento;
  motivos: string[];
  serie: string | null;
  identificacion: string | null;
  actual: LadoOlt | null;
  nueva: LadoOlt | null;
  payload_consulta?: Record<string, unknown>;
  payload_ejecucion?: Record<string, unknown>;
  consulta?: SolicitudMigracion | null;
  ejecucion?: SolicitudMigracion | null;
};

const urlOrden = (idOrdenTrabajo: string) =>
  `${urlBackend}api/v1/orden-trabajo/${idOrdenTrabajo}/olt-traslado/`;

/** Qué OLT se usarían y por qué camino iría, sin tocar nada. */
export async function consultarOltTraslado(
  idOrdenTrabajo: string,
): Promise<AprovisionamientoTraslado> {
  const res = await get<AprovisionamientoTraslado>(urlOrden(idOrdenTrabajo), true);
  return res.data;
}

/** Paso 1 de A: consulta contra la OLT donde la ONT está hoy. */
export async function consultarMigracionTraslado(
  idOrdenTrabajo: string,
): Promise<AprovisionamientoTraslado> {
  const res = await post<AprovisionamientoTraslado>(
    `${urlOrden(idOrdenTrabajo)}consultar/`, {} as any, true,
  );
  if (res.status === 'error') {
    throw new Error(res.message || 'No se pudo consultar la migración.');
  }
  return res.data;
}

/** Paso 2 de A: aprovisiona en la OLT nueva. Corta el servicio mientras corre. */
export async function ejecutarMigracionTraslado(
  idOrdenTrabajo: string,
): Promise<AprovisionamientoTraslado> {
  const res = await post<AprovisionamientoTraslado>(
    `${urlOrden(idOrdenTrabajo)}ejecutar/`, {} as any, true,
  );
  if (res.status === 'error') {
    throw new Error(res.message || 'No se pudo lanzar la migración.');
  }
  return res.data;
}

/** Cómo va la solicitud. Se consulta al micro por el gateway. */
export async function estadoMigracion(
  idSolicitud: string,
): Promise<SolicitudMigracion> {
  const res = await get<SolicitudMigracion>(
    `${urlBackend}api/v1/gestion-tickets/clientes/migracion-olt/api/migrar/${idSolicitud}`,
    true,
  );
  return res.data;
}

/** Estados en los que la serie quedó bien. */
const ESTADOS_OK = ['MIGRADO', 'MIGRADO_AVISO'];
/** La consulta dice que sí se migraría. */
export const ESTADO_LISTO = 'LISTO';

export function serieDe(solicitud?: SolicitudMigracion | null): ResultadoSerie | null {
  const lista = solicitud?.resultados;
  return Array.isArray(lista) && lista.length > 0 ? lista[0] : null;
}

export function migracionTerminoBien(solicitud?: SolicitudMigracion | null): boolean {
  const r = serieDe(solicitud);
  return !!r && ESTADOS_OK.includes(String(r.estado).toUpperCase());
}

/** Color del chip según cómo quedó la serie. */
export function colorEstadoSerie(
  estado?: string,
): 'success' | 'warning' | 'error' | 'info' | 'default' {
  const e = String(estado || '').toUpperCase();
  if (ESTADOS_OK.includes(e)) return 'success';
  if (e === ESTADO_LISTO) return 'info';
  if (['EN_PROCESO', 'EN_COLA', 'MIGRANDO'].includes(e)) return 'info';
  if (['SIN_SERVICIO', 'ERROR', 'ERROR_OLT', 'ERROR_SOFTNET'].includes(e)) return 'error';
  if (!e) return 'default';
  return 'warning';
}
